import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import type { Appointment, Service, Staff } from "@/api/types";
import { useStore } from "@/store/StoreContext";
import { Badge, tx } from "@/components/ui";
import { DateInput } from "@/components/DateInput";
import { FilterBar, type Filter } from "@/components/FilterBar";
import { AppointmentDrawer, type DrawerState } from "@/components/AppointmentDrawer";
import { Pager } from "@/components/Pager";
import { addDays, fmtDate, mondayOf, toIso, todayIso } from "@/lib/dates";

const PAGE = 50;
const DEFAULT_COLS = ["date", "time", "customer", "service", "staff", "status", "source", "price"];

export function AppointmentsPage() {
  const { store } = useStore();
  const { t, i18n } = useTranslation();
  const [from, setFrom] = useState(todayIso());
  const [to, setTo] = useState(addDays(todayIso(), 7));
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState<Filter[]>([]);
  const [columns, setColumns] = useState<string[]>(DEFAULT_COLS);
  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const q = new URLSearchParams({ store_id: store?.id ?? "", from, to, limit: String(PAGE + 1), offset: String(page * PAGE) });
  if (filters.length) q.set("filters", JSON.stringify(filters));
  const rows = useQuery({ queryKey: ["appointments-list", q.toString()], queryFn: () => api<Appointment[]>(`/app/appointments?${q}`), enabled: !!store });
  const services = useQuery({ queryKey: ["services", store?.id], queryFn: () => api<Service[]>(`/app/services?store_id=${store!.id}`), enabled: !!store });
  const staff = useQuery({ queryKey: ["staff", store?.id], queryFn: () => api<Staff[]>(`/app/staff?store_id=${store!.id}`), enabled: !!store });
  const cal = useQuery({ queryKey: ["calendar", store?.id, from], queryFn: () => api<{ columns: { staff_id: string; display_name: string; color: string; shift_start: string | null; shift_end: string | null }[]; grid_step: number }>(`/app/calendar?store_id=${store!.id}&date=${from}`), enabled: !!store });
  if (!store) return null;
  const list = (rows.data ?? []).slice(0, PAGE);
  const setRange = (f: string, t2: string) => { setFrom(f); setTo(t2); setPage(0); };
  const now = new Date();
  const presets: [string, string, string][] = [["today", todayIso(), todayIso()], ["tomorrow", addDays(todayIso(), 1), addDays(todayIso(), 1)], ["thisWeek", mondayOf(todayIso()), addDays(mondayOf(todayIso()), 6)], ["next7", todayIso(), addDays(todayIso(), 7)], ["thisMonth", toIso(new Date(now.getFullYear(), now.getMonth(), 1)), toIso(new Date(now.getFullYear(), now.getMonth() + 1, 0))]];
  const svc = (id: string) => { const s = services.data?.find((x) => x.id === id); return s ? tx(s.name, i18n.language) : ""; };
  const st = (id: string | null) => staff.data?.find((x) => x.id === id)?.display_name ?? "";
  const cell = (a: Appointment, c: string) => {
    const d = new Date(a.starts_at);
    switch (c) {
      case "date": return fmtDate(d, i18n.language, store.tz);
      case "time": return d.toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit", timeZone: store.tz });
      case "customer": return `${a.customer.first_name} ${a.customer.last_name}`;
      case "service": return svc(a.service_id);
      case "staff": return st(a.staff_id);
      case "status": return <Badge tone={a.status === "completed" ? "green" : a.status === "cancelled" || a.status === "no_show" ? "red" : a.status === "checked_in" ? "green" : "blue"}>{t(`status.${a.status}`)}</Badge>;
      case "source": return t(`source.${a.source}`);
      case "price": return `${a.price_quoted}€`;
      case "duo": return a.group_id ? "duo" : "";
      case "conflict": return a.conflict ? "⚠" : "";
      case "private": return a.private ? "●" : "";
      case "staff_choice": return a.source === "online" ? (a.preferred_staff_id ? t("appt.choseShort") : t("appt.anyone")) : "";
      case "notes": return a.notes ?? "";
      default: return c.startsWith("custom.") ? String((a as Appointment & { custom_values?: Record<string, unknown> }).custom_values?.[c.slice(7)] ?? "") : "";
    }
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-2xl font-semibold mr-2">{t("nav.appointments")}</h1>
        <DateInput className="!w-auto" value={from} onChange={(e) => setRange(e.target.value, to)} /> → <DateInput className="!w-auto" value={to} onChange={(e) => setRange(from, e.target.value)} />
        <span className="text-sm text-muted">{list.length}{(rows.data?.length ?? 0) > PAGE ? "+" : ""} {t("calendar.appointments")}</span>
      </div>
      <div className="flex flex-wrap gap-1">{presets.map(([k, f, t2]) => <button key={k} type="button" className={`rounded-full border px-2.5 py-0.5 text-xs ${from === f && to === t2 ? "border-accent bg-accent text-white" : "border-line-strong bg-surface hover:bg-surface-2"}`} onClick={() => setRange(f, t2)}>{t(`range.${k}`)}</button>)}</div>
      <FilterBar brandId={store.brand_id} storeId={store.id} entity="appointments" filters={filters} onChange={(f) => { setFilters(f); setPage(0); }} columns={columns} onColumns={setColumns} />
      <div className="overflow-auto rounded-xl border bg-surface">
        <table className="w-full text-sm">
          <thead className="bg-surface-2 text-left"><tr>{columns.map((c) => <th key={c} className="p-2 whitespace-nowrap">{t(`col.${c}`, { defaultValue: c.replace("custom.", "") })}</th>)}</tr></thead>
          <tbody>{list.map((a) => <tr key={a.id} className="border-t hover:bg-surface-2 cursor-pointer" onClick={() => setDrawer({ mode: "edit", appointment: a })}>{columns.map((c) => <td key={c} className="p-2 whitespace-nowrap">{cell(a, c)}</td>)}</tr>)}</tbody>
        </table>
      </div>
      <Pager page={page} pageSize={PAGE} count={rows.data?.length ?? 0} onPage={setPage} />
      <AppointmentDrawer state={drawer} onClose={() => { setDrawer(null); rows.refetch(); }} store={store} gridStep={cal.data?.grid_step ?? 15} services={services.data ?? []} columns={cal.data?.columns ?? []} />
    </div>
  );
}
